import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Lock, Loader2, AlertCircle, CheckCircle } from 'lucide-react';
import { supabase } from '../../lib/supabase';

export default function ResetPassword() {
  const navigate = useNavigate();
  const location = useLocation();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [tokenVerified, setTokenVerified] = useState(false);

  useEffect(() => {
    const verifyToken = async () => {
      setLoading(true);
      setError(null);

      const searchParams = new URLSearchParams(location.search);
      const token = searchParams.get('token');
      const type = searchParams.get('type');
      const email = searchParams.get('email');

      const hashParams = new URLSearchParams(window.location.hash.substring(1));
      const accessToken = hashParams.get('access_token');
      const refreshToken = hashParams.get('refresh_token');
      const typeHash = hashParams.get('type');

      try {
        if (accessToken && typeHash === 'recovery') {
          const { error } = await supabase.auth.setSession({
            access_token: accessToken,
            refresh_token: refreshToken || '',
          });

          if (error) throw error;

          setTokenVerified(true);
        } else if (token && type === 'recovery' && email) {
          const { data, error: verifyError } = await supabase.auth.verifyOtp({
            token,
            type: 'recovery',
            email
          });

          if (verifyError) throw verifyError;

          if (data?.session) {
            await supabase.auth.setSession(data.session);
            setTokenVerified(true);
          } else {
            throw new Error('No session returned from token verification');
          }
        } else {
          setError('Invalid or missing reset token or email. Please request a new password reset link.');
        }
      } catch (err: any) {
        console.error('Token verification error:', err);
        setError(err.message || 'Invalid or expired reset link. Please request a new one.');
      } finally {
        setLoading(false);
      }
    };

    verifyToken();
  }, [location.search]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (loading) return;

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const { error: updateError } = await supabase.auth.updateUser({
        password: password
      });

      if (updateError) throw updateError;

      setSuccess(true);

      setTimeout(() => {
        navigate('/login');
      }, 3000);
    } catch (err: any) {
      console.error('Password update error:', err);
      setError(err.message || 'Failed to update password. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-400 via-purple-500 to-indigo-600 flex items-center justify-center px-4">
      <div className="max-w-md w-full bg-white rounded-lg shadow-xl p-8">
        <div className="text-center mb-8">
          <h2 className="text-3xl font-bold text-gray-900">Reset Your Password</h2>
          <p className="text-gray-600 mt-2">
            Enter your new password below
          </p>
        </div>

        {error && (
          <div className="mb-6 p-4 bg-red-50 border-l-4 border-red-500 text-red-700 flex items-center">
            <AlertCircle className="w-5 h-5 mr-2" />
            <p>{error}</p>
          </div>
        )}

        {success ? (
          <div className="mb-6 p-4 bg-green-50 border-l-4 border-green-500 text-green-700">
            <div className="flex items-center mb-2">
              <CheckCircle className="w-5 h-5 mr-2" />
              <p className="font-medium">Password Updated Successfully</p>
            </div>
            <p>
              Your password has been reset. You will be redirected to the login page shortly.
            </p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">New Password</label>
              <div className="mt-1 relative">
                <input
                  type="password"
                  required
                  disabled={!tokenVerified || loading}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-purple-500 focus:border-purple-500"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  minLength={6}
                />
                <Lock className="w-5 h-5 text-gray-400 absolute right-3 top-2.5" />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Confirm New Password</label>
              <div className="mt-1 relative">
                <input
                  type="password"
                  required
                  disabled={!tokenVerified || loading}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-purple-500 focus:border-purple-500"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  minLength={6}
                />
                <Lock className="w-5 h-5 text-gray-400 absolute right-3 top-2.5" />
              </div>
            </div>

            <button
              type="submit"
              disabled={loading || !tokenVerified}
              className="w-full bg-purple-600 text-white py-2 px-4 rounded-lg hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center"
            >
              {loading ? (
                <>
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  {tokenVerified ? 'Updating...' : 'Verifying...'}
                </>
              ) : (
                'Reset Password'
              )}
            </button>
          </form>
        )}

        {!tokenVerified && !success && !loading && (
          <div className="mt-6 text-center">
            <button
              onClick={() => navigate('/forgot-password')}
              className="text-purple-600 hover:text-purple-700 font-medium"
            >
              Request a new password reset link
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
